import React, { useCallback, useState, useEffect } from 'react';
import { Upload } from 'tus-js-client';
import { Box, Button, Progress, Text, useToast } from '@chakra-ui/react';
import type { InstanceConfig } from '../App';
import { instanceConfigs } from '../App';

interface FileUploaderProps {
  onUploadComplete: (imageUrl: string) => void;
}

interface FileResponse {
  file: {
    url: string;
    displayName: string;
    media: {
      image: {
        image: {
          url: string;
          width: number;
          height: number;
        }
      }
    }
  }
}

export const FileUploader: React.FC<FileUploaderProps> = ({ onUploadComplete }) => {
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [instanceName, setInstanceName] = useState<string>('startchat-button');
  const toast = useToast();

  useEffect(() => {
    const path = window.location.pathname;
    const instance = path.split('/').filter(Boolean)[0] || 'startchat-button';
    setInstanceName(instance);
  }, []);

  const handleFileUpload = useCallback(async (file: File) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      // Convert instanceConfigs object to array before using find
      const baseUrl = Object.values(instanceConfigs).find((instance: InstanceConfig) => instance.client_id === instanceName)?.baseUrl;
      const response = await fetch(`${baseUrl}/_functions/uploadurl`);
      const { uploadUrl, uploadToken } = await response.json();

      // Create a new tus upload
      await new Promise((resolve, reject) => {
        const upload = new Upload(file, {
          endpoint: uploadUrl,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          metadata: {
            filename: file.name,
            contentType: file.type,
            token: uploadToken
          },
          onError: (error) => {
            console.error('Upload failed:', error);
            reject(error);
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
            setUploadProgress(parseFloat(percentage));
          },
          onSuccess: () => {
            console.log('Resumable upload completed');
            resolve(true);
          },
        });

        upload.start();
      });

      // Finalize the upload with a PUT request
      const finalizeUrl = new URL(`${uploadUrl}/${uploadToken}`);
      finalizeUrl.searchParams.append('filename', file.name);

      const finalizeResponse = await fetch(finalizeUrl.toString(), {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file
      });

      if (!finalizeResponse.ok) {
        const errorData = await finalizeResponse.json();
        throw new Error(errorData.message || 'Failed to finalize upload');
      }

      const responseData: FileResponse = await finalizeResponse.json();
      const imageUrl = responseData.file.media.image.image.url;
      console.log('Upload finished:', imageUrl);
      
      // Create an img tag with proper dimensions and fit properties
      const width = responseData.file.media.image.image.width;
      const height = responseData.file.media.image.image.height;
      const avatarHtml = `<img 
        src="${imageUrl}" 
        alt="${responseData.file.displayName}" 
        width="${width}" 
        height="${height}" 
        style="object-fit: contain; max-width: 100%; height: auto;"
      />`;
      
      onUploadComplete(avatarHtml);
      setIsUploading(false);
      toast({
        title: 'Upload complete',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error during upload:', error);
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Please try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsUploading(false);
    }
  }, [onUploadComplete, toast, instanceName]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadProgress(0);
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  return (
    <Box>
      <input
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        id="file-upload"
        disabled={isUploading}
      />
      <label htmlFor="file-upload">
        <Button
          as="span"
          colorScheme="blue"
          isLoading={isUploading}
          loadingText="Uploading..."
          cursor="pointer"
        >
          Upload Avatar
        </Button>
      </label>
      
      {isUploading && (
        <Box mt={2}>
          <Progress value={uploadProgress} size="sm" colorScheme="blue" />
          <Text fontSize="sm" mt={1}>
            {uploadProgress.toFixed(0)}% uploaded
          </Text>
        </Box>
      )}
    </Box>
  );
};
